import React, { useEffect, useState } from "react";
import * as Type from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Type";
import * as Flex from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Flex";
import * as Button from "../../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import Preloader, * as PreloaderStyles from "../../../../../../../common-deprecated/styles/v2/toyota/custom/Preloader";
import * as PacksView from "./styles/Packs";
import {
    IconWrapper,
    ToggleIconWrapper,
} from "../../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import Icon from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { BnbLabel } from "../../../../utils/labels";
import Collapsable from "../../common/collapsable/Collapsable";
import * as FinanceStyles from "./styles/Finance";
import type { FinanceInsuranceCalculatorType } from "../../../../utils/FinanceInsuranceCalculatorLink";
import { INSURANCE_CALCULATOR } from "../../../../../../../common-deprecated/constants";
import FinanceInsuranceCalculator from "../../../common/FinanceInsuranceCalculator";
import { useBnbSelector } from "../../../../redux/store";
import { setInsuranceCollapsable } from "../../../../redux/actions/FinanceInsuranceActions";
import { MODAL_FINANCE_DISCLAIMER } from "../../../../../../../common-deprecated/utils/modalConstants";
import { showModal } from "../../../../../../../common-deprecated/redux/actions/ModalActions";
import InsurancePrice from "../../common/price/InsurancePrice";
import { hasInsuranceConfigured, showFinanceInsurance } from "../../../../utils/financeInsuranceUtils";
import { trackClickedLink, trackInPageCalculator } from "../../../../utils/tracking";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";

type PropsType = {
    calculatorType?: FinanceInsuranceCalculatorType;
    expandedByDefault?: boolean; // When provided the finance section is expanded by default
    showIntroduction?: boolean;
};
const Insurance = ({
    calculatorType = INSURANCE_CALCULATOR,
    expandedByDefault = false,
    showIntroduction = true,
}: PropsType): JSX.Element | null => {
    const { fetching, collapsableActive, isPersonalizedQuote, info } = useBnbSelector((state) => state.insuranceRate);
    const { commonSettings, bnbSettings, car } = useBnbSelector((state) => state);
    const { requireDefaultInsurance } = useBnbSelector((state) => state.bnbSettings);
    const step = useBnbSelector((state) => state.navigation.step);
    const finance = useBnbSelector((state) => state.finance);
    const [insuranceDetailsExpanded, setInsuranceDetailsExpanded] = useState<boolean>(false);
    const dispatch = useDispatch();

    const insuranceIsSet = hasInsuranceConfigured(requireDefaultInsurance, isPersonalizedQuote);

    useEffect(() => {
        if (expandedByDefault && !collapsableActive) {
            dispatch(setInsuranceCollapsable(true));
        }
    }, []);

    if (
        !showFinanceInsurance({
            type: "insurance",
            bnbSettings,
            car,
            commonSettings,
            finance,
            step,
        })
    ) {
        return null;
    }

    // TRIGGERS AFTER THE COLLAPSABLE HAS EXPANDED/CLOSED
    const onChange = (expanded: boolean): void => {
        dispatch(setInsuranceCollapsable(expanded));
    };

    // Only Triggers after the collapsable button is clicked to open/close the collapsable
    const onTrack = (): void => {
        const trackingLabel = !collapsableActive
            ? "open-inpage-insurance-calculator"
            : "close-inpage-insurance-calculator";
        trackInPageCalculator(!collapsableActive ? "cc-cta" : "cc-action", trackingLabel, step);
    };

    return (
        <FinanceStyles.Finance id="insurance-injection-container">
            {showIntroduction && (
                <PacksView.Header>
                    <Flex.Col width={{ sm: 7 / 12, md: 1 }}>
                        <Type.H2 headingType="gamma">
                            <BnbLabel label="insuranceInPageTitle" />
                        </Type.H2>
                        <Type.Text my={3}>
                            <BnbLabel label="insuranceInPageText" useHTML />
                        </Type.Text>
                        <details
                            onToggle={() => {
                                setInsuranceDetailsExpanded(!insuranceDetailsExpanded);
                                if (!insuranceDetailsExpanded) {
                                    trackClickedLink("why-insurance");
                                }
                            }}
                        >
                            <FinanceStyles.Summary>
                                <Button.Link my={3} hasIconRight as="a">
                                    <BnbLabel label="insuranceInPageDetailsLabel" />
                                    <ToggleIconWrapper isDown={insuranceDetailsExpanded}>
                                        <Icon variant="chevronDown" />
                                    </ToggleIconWrapper>
                                </Button.Link>
                            </FinanceStyles.Summary>
                            <Type.SmallText my={3}>
                                <BnbLabel label="insuranceInPageDetails" useHTML />
                            </Type.SmallText>
                        </details>
                    </Flex.Col>
                </PacksView.Header>
            )}
            <Flex.Row my={4}>
                <Flex.Col width={1}>
                    <Collapsable
                        title={
                            <Type.H4 mb={0}>
                                <BnbLabel label="insuranceCollapsableTitle" />
                            </Type.H4>
                        }
                        subtitle={
                            insuranceIsSet ? (
                                <Flex.Row>
                                    <Flex.Col display="flex">
                                        <div>
                                            <InsurancePrice forceHideDisclaimer />
                                        </div>
                                        {info && info.disclaimer && (
                                            <Button.Link
                                                type="button"
                                                hasIconRight
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    if (info?.disclaimer) {
                                                        dispatch(
                                                            showModal({
                                                                type: MODAL_FINANCE_DISCLAIMER,
                                                                disclaimer: info.disclaimer,
                                                                productName: "",
                                                            }),
                                                        );
                                                    }
                                                }}
                                            >
                                                <IconWrapper>
                                                    <Icon variant="info" />
                                                </IconWrapper>
                                            </Button.Link>
                                        )}
                                    </Flex.Col>
                                </Flex.Row>
                            ) : undefined
                        }
                        ctaLabel={
                            <BnbLabel label={isPersonalizedQuote ? "insuranceButtonEdit" : "insuranceCollapsableCta"} />
                        }
                        icon={
                            <IconWrapper>
                                <Icon variant="insurance" size="medium" />
                            </IconWrapper>
                        }
                        content={
                            <FinanceInsuranceCalculator
                                type={calculatorType}
                                visible={collapsableActive}
                                showLoader={fetching}
                                loader={
                                    <PreloaderStyles.PreLoaderWrapper hasBorder hasButtonHeight>
                                        <Preloader size={30} />
                                    </PreloaderStyles.PreLoaderWrapper>
                                }
                            />
                        }
                        active={collapsableActive}
                        onChange={onChange}
                        onTrack={onTrack}
                    />
                </Flex.Col>
            </Flex.Row>
        </FinanceStyles.Finance>
    );
};

export default Insurance;
